import * as React from 'react';
import clsx from 'clsx';

import config from 'config';
import { useSelector } from 'react-redux';
import styles from './Header.module.css';
import { ReduxState } from 'ducks';
import { useMapDisplayContext } from '../MapDisplayContext';
import { Menu } from './Menu';
import { HamburgerIcon } from './HamburgerIcon';
import { useTranslation } from 'react-i18next';
import { DesktopSearch } from './DesktopSearch';
import { NotificationsIcon } from './NotificationsIcon';
import { OrdersIcon } from './OrdersIcon';
import { LanguageSelect } from './LanguageSelect';
import { CustomMenuItem } from 'models/digitalMap';
import { SubmenuItem } from './SubmenuItem';
import { useDigitalMapCustomMenuItems } from '../useDigitalMapCustomMenuItems';
import { StarIcon } from './StarIcon';
import { isItineraryCreatorEnabled } from '../util';

export const Header = () => {
  const {
    setDisplayMode,
    resetDisplay,
    menuExpanded,
    setMenuExpanded,
    activePage,
    setActivePage,
    setShowStampRallyModal,
    showStampRallyModal,
    activePinKey,
    showStarredPinList,
    setShowStarredPinList,
  } = useMapDisplayContext();
  const { t } = useTranslation();

  const digitalMap = useSelector((state: ReduxState) => state.universal.digitalMap.map);
  const stampRally = useSelector(
    (state: ReduxState) => state.universal.digitalGuidanceStampRally.stampRally
  );

  const customMenuItems = useDigitalMapCustomMenuItems();

  const settings = useSelector((state: ReduxState) => state.server.settings.all);

  const restaurantOrders = useSelector((state: ReduxState) => state.restaurantOrders.orders);

  const isMapTopPage = !activePinKey;

  const getMenuItem = (item: CustomMenuItem) => {
    switch (item.item_type) {
      case 'MAP':
        return (
          <div
            onClick={() => {
              resetDisplay();
              setActivePage('MAP');
            }}
            className={clsx(styles['desktop-item'], activePage === 'MAP' && styles['active'])}
          >
            {t('Map')}
          </div>
        );
      case 'LIST':
        return (
          <div
            onClick={() => {
              resetDisplay();
              setActivePage('LIST');
            }}
            className={clsx(styles['desktop-item'], activePage === 'LIST' && styles['active'])}
          >
            {t('List', {
              context: 'map-menu',
            })}
          </div>
        );
      case 'MY_ACCOUNT':
        return (
          <div
            onClick={() => {
              resetDisplay();
              setActivePage('ACCOUNT');
            }}
            className={clsx(styles['desktop-item'], activePage === 'ACCOUNT' && styles['active'])}
          >
            {t('My Account')}
          </div>
        );
      case 'TICKETS':
        return (
          <div
            onClick={() => {
              resetDisplay();
              setActivePage('TICKETS');
            }}
            className={clsx(styles['desktop-item'], activePage === 'TICKETS' && styles['active'])}
          >
            {t('Tickets')}
          </div>
        );
      case 'SCHEDULE':
        return (
          <div
            onClick={() => {
              resetDisplay();
              setActivePage('SCHEDULE');
            }}
            className={clsx(styles['desktop-item'], activePage === 'SCHEDULE' && styles['active'])}
          >
            {t('Schedule')}
          </div>
        );
      case 'STAMP_RALLY':
        return (
          stampRally && (
            <div
              onClick={() => {
                resetDisplay();
                setShowStampRallyModal(true);
              }}
              className={clsx(styles['desktop-item'], showStampRallyModal && styles['active'])}
            >
              {t('Stamp Rally')}
            </div>
          )
        );
      case 'ITINERARIES':
        return (
          <div
            onClick={() => {
              resetDisplay();
              setActivePage('ITINERARIES.LIST');
            }}
            className={clsx(
              styles['desktop-item'],
              activePage.startsWith('ITINERARIES') && styles['active']
            )}
          >
            {t('Itineraries')}
          </div>
        );
      case 'CUSTOM_SUBMENU':
        return <SubmenuItem item={item} />;
      case 'CUSTOM_LINK':
        return (
          <div
            onClick={() => {
              window.open(item.url, '_blank');
            }}
            className={clsx(styles['desktop-item'])}
          >
            {item.label}
          </div>
        );
    }
  };

  return (
    <>
      <section className={clsx(styles['container'], menuExpanded && styles['expanded'])}>
        <div className={styles['main-content']}>
          {!menuExpanded &&
            (digitalMap?.seo_params?.title ? (
              isMapTopPage ? (
                <div>
                  <img
                    src={settings.logo_url}
                    alt="Logo"
                    className={styles['logo']}
                    onClick={() => {
                      resetDisplay();
                    }}
                  />

                  <h1 className={styles['title']}>{digitalMap.seo_params.title}</h1>
                </div>
              ) : (
                <div>
                  <img
                    src={settings.logo_url}
                    alt="Logo"
                    className={styles['logo']}
                    onClick={() => {
                      resetDisplay();
                    }}
                  />
                  <div className={styles['title']}>{digitalMap.seo_params.title}</div>
                </div>
              )
            ) : (
              <img
                src={settings.logo_url}
                alt="Logo"
                className={styles['logo']}
                onClick={() => {
                  resetDisplay();
                }}
              />
            ))}
          <aside className={styles['desktop-items']}>{customMenuItems?.map(getMenuItem)}</aside>

          {(digitalMap?.display_settings?.language_menu_items?.length ?? 0) > 0 && (
            <div className={styles['desktop-only']}>
              <LanguageSelect key={digitalMap?.seo_params?.language} />
            </div>
          )}
          {isItineraryCreatorEnabled(digitalMap) && (
            <div className={styles['desktop-only']}>
              <StarIcon
                onClick={() => {
                  resetDisplay();
                  setShowStarredPinList(!showStarredPinList);
                }}
              />
            </div>
          )}
          {config.enableIAAPADemo && (
            <div className={styles['desktop-only']}>
              <NotificationsIcon
                onClick={() => {
                  resetDisplay();
                  setActivePage('NOTIFICATIONS');
                }}
              />
            </div>
          )}
          {config.enableIAAPADemo && restaurantOrders.length > 0 && (
            <div className={styles['desktop-only']}>
              <OrdersIcon
                onClick={() => {
                  resetDisplay();
                  setActivePage('ORDERS');
                }}
              />
            </div>
          )}
          <DesktopSearch />
          <div className={styles['mobile-icons']}>
            {isItineraryCreatorEnabled(digitalMap) && (
              <StarIcon
                onClick={() => {
                  resetDisplay();
                  setShowStarredPinList(!showStarredPinList);
                }}
              />
            )}
            {!menuExpanded && (
              <>
                <img
                  onClick={() => {
                    resetDisplay();
                    setDisplayMode('SEARCH_INPUT');
                  }}
                  src="/static/images/map/search.svg"
                  alt="Sub icon 1"
                  className={styles['icon']}
                />
              </>
            )}
            {config.enableIAAPADemo && (
              <NotificationsIcon
                onClick={() => {
                  resetDisplay();
                  setActivePage('NOTIFICATIONS');
                }}
              />
            )}
            {config.enableIAAPADemo && restaurantOrders.length > 0 && (
              <OrdersIcon
                onClick={() => {
                  resetDisplay();
                  setActivePage('ORDERS');
                }}
              />
            )}
            <HamburgerIcon onClick={() => setMenuExpanded(!menuExpanded)} isCross={menuExpanded} />
          </div>
        </div>
        {menuExpanded && <Menu onClose={() => setMenuExpanded(false)} />}
      </section>
    </>
  );
};
